import React from 'react';
import type { AppUser } from '../stores/authStore';

// ─── Props (동일 인터페이스) ────────────────────────────────────────────────────
interface EducationEntry {
  id: string; type: 'COLLEGE' | 'GRADUATE'; year: string;
  schoolName: string; major: string; status: 'GRADUATED' | 'COMPLETED' | 'ENROLLED';
  degreeLevel?: 'MASTERS' | 'DOCTORATE';
  degreeReviewStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
}
interface ExperienceEntry { id: string; year: string; company: string; position: string; }
interface AwardEntry { id: string; year: string; title: string; }
interface CertificateEntry {
  id: string; name: string; issuer: string; date: string;
  reviewStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
}
interface Visibility {
  education: boolean; experience: boolean; awards: boolean;
  certificates_list: boolean; introduction: boolean;
  stampfit_certificates: boolean; portfolio: boolean;
}
export interface ResumeTemplate3Props {
  user: AppUser | null;
  photoUrl: string | null;
  education: EducationEntry[];
  experience: ExperienceEntry[];
  awards: AwardEntry[];
  certificatesList: CertificateEntry[];
  stampfitCerts: any[];
  introduction: string;
  visibility: Visibility;
  desiredPosition: string;
  address: string;
  getPortfolioDisplayUrl: (slot: number) => string;
}

// ─── Layout constants ─────────────────────────────────────────────────────────
const PAGE_W = 1123; // A4 landscape
const PAGE_H = 794;
const P1_PHOTO_W = 695;
const P1_INFO_W = PAGE_W - P1_PHOTO_W; // 428
const P2_CONTENT_W = 675;
const P2_PHOTO_W = PAGE_W - P2_CONTENT_W; // 448
const P3_PHOTO_W = 472;
const P3_CONTENT_W = PAGE_W - P3_PHOTO_W; // 651

// ─── Design tokens ────────────────────────────────────────────────────────────
const DARK_BG = '#1a2636';
const NOTO = "'Noto Sans KR', sans-serif";
const NOTO_EN = "'Helvetica Neue', 'Noto Sans KR', sans-serif";
const TEXT_WHITE = '#eff2f6';
const TEXT_BODY = '#dce6f0';
const TEXT_LABEL = '#7a9eb8';
const TEXT_MUTED = '#8aacbf';
const TEXT_ISS = '#5a7e98';
const DIVIDER = 'rgba(255,255,255,0.22)';

const SEC_TTL_W = 112;
const SEC_TTL_EN_W = 148;
const SEC_GAP = 18;
const DATE_W_ROW = 132;
const DATE_W_CERT = 84;

const STATUS_LABEL: Record<string, string> = { GRADUATED: '졸업', COMPLETED: '수료', ENROLLED: '재학' };
const DEGREE_LABEL: Record<string, string> = { MASTERS: '석사', DOCTORATE: '박사' };

// ─── Helpers ──────────────────────────────────────────────────────────────────
function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
}
function formatPhone(phone?: string): string {
  if (!phone) return '';
  const d = phone.replace(/\D/g, '');
  if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return phone;
}
function formatBirthDate(date?: string): string {
  if (!date) return '';
  const p = date.split('-');
  if (p.length === 3) return `${p[0]}. ${parseInt(p[1], 10)}. ${parseInt(p[2], 10)}`;
  return date;
}
function certDate(date: string): string {
  if (!date) return '';
  const p = date.split('-');
  if (p.length === 3) return `${p[0]}. ${parseInt(p[1], 10)}. ${parseInt(p[2], 10)}`;
  return date.replace(/-/g, '. ');
}

// ─── Badge ────────────────────────────────────────────────────────────────────
function Badge3({ label, variant }: { label: string; variant: 'cert' | 'warn' | 'degree' }) {
  const bg = variant === 'cert' ? '#d96a30' : variant === 'warn' ? '#b87800' : '#296b3e';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      background: bg, color: '#fff',
      fontSize: 11, fontWeight: 400,
      padding: '2px 7px', borderRadius: 3,
      verticalAlign: 'middle', lineHeight: 1.35,
      letterSpacing: '.02em', whiteSpace: 'nowrap', flexShrink: 0,
      fontFamily: NOTO,
    }}>
      {variant === 'warn' ? `△ ${label}` : label}
    </span>
  );
}

// ─── Section row ──────────────────────────────────────────────────────────────
function SectionRow({
  title, titleWidth, showDivider = true, children,
}: {
  title: React.ReactNode;
  titleWidth?: number;
  showDivider?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'row', gap: SEC_GAP,
      padding: '20px 0', flexShrink: 0,
      borderTop: showDivider ? `1px solid ${DIVIDER}` : 'none',
    }}>
      <div style={{
        width: titleWidth ?? SEC_TTL_W, flexShrink: 0,
        fontSize: 20, fontWeight: 100, color: TEXT_WHITE,
        lineHeight: 1.4, letterSpacing: '-.01em',
        wordBreak: 'keep-all', fontFamily: NOTO,
      }}>
        {title}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 11, fontFamily: NOTO }}>
        {children}
      </div>
    </div>
  );
}

// ─── Experience / Award row ──────────────────────────────────────────────────
function Row2({ date, content }: { date: string; content: string }) {
  return (
    <div style={{ display: 'flex', gap: 14, fontSize: 14, lineHeight: 1.5, fontWeight: 100 }}>
      <span style={{ color: TEXT_LABEL, width: DATE_W_ROW, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{date}</span>
      <span style={{ color: TEXT_BODY }}>{content}</span>
    </div>
  );
}

// ─── Certificate row ──────────────────────────────────────────────────────────
function CertRow({ date, name, issuer, approved }: { date: string; name: string; issuer?: string; approved: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 14, fontSize: 14, lineHeight: 1.5, fontWeight: 100 }}>
      <span style={{ color: TEXT_LABEL, width: DATE_W_CERT, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{date}</span>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: TEXT_BODY, flexWrap: 'wrap' }}>
          <span>{name}</span>
          <Badge3 label={approved ? '인증' : '미인증'} variant={approved ? 'cert' : 'warn'} />
        </div>
        {issuer && <div style={{ fontSize: 12, color: TEXT_ISS, marginTop: 2 }}>{issuer}</div>}
      </div>
    </div>
  );
}

// ─── Photo panel ─────────────────────────────────────────────────────────────
function PhotoPanel({ url, width }: { url: string; width: number }) {
  return (
    <div style={{ width, height: PAGE_H, flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
      {url ? (
        <img src={url} alt="포트폴리오" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
      ) : (
        <div style={{ width: '100%', height: '100%', background: 'linear-gradient(155deg,#2d404e 0%,#3c5060 48%,#1c2c3a 100%)' }} />
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function ResumeTemplate3Preview(props: ResumeTemplate3Props) {
  const {
    user, desiredPosition, address, introduction,
    education, experience, awards,
    certificatesList, stampfitCerts,
    visibility, getPortfolioDisplayUrl,
  } = props;

  const nameEn = (user?.name_en || '').toUpperCase();
  const birthDate = formatBirthDate(user?.birth_date);
  const phone = formatPhone(user?.phone);
  const email = user?.email || '';
  const introText = stripHtml(introduction).trim();

  const infoItems = [
    ...(birthDate ? [{ label: 'Date of Birth', value: `· ${birthDate}` }] : []),
    ...(phone ? [{ label: 'Phone', value: `· ${phone}` }] : []),
    ...(address ? [{ label: 'Address', value: `· ${address}` }] : []),
    ...(email ? [{ label: 'E-mail', value: `· ${email}` }] : []),
  ];

  const colleges = education.filter(e => e.type === 'COLLEGE');
  const graduates = education.filter(e => e.type === 'GRADUATE');
  const hasEdu = visibility.education && (colleges.length > 0 || graduates.length > 0);
  const hasExp = visibility.experience && experience.length > 0;
  const hasAward = visibility.awards && awards.length > 0;
  const hasCert = visibility.certificates_list && certificatesList.length > 0;
  const hasSF = visibility.stampfit_certificates && stampfitCerts.length > 0;

  const photo1 = getPortfolioDisplayUrl(0);
  const photo2 = getPortfolioDisplayUrl(1);
  const photo3 = getPortfolioDisplayUrl(2);

  const dark = (w: number) => ({
    width: w, height: PAGE_H, background: DARK_BG,
    flexShrink: 0, overflow: 'hidden',
    display: 'flex', flexDirection: 'column' as const,
    fontFamily: NOTO,
  });

  return (
    <div
      className="resume-template-root"
      style={{ display: 'flex', flexDirection: 'column', gap: 24, fontFamily: NOTO, fontWeight: 100 }}
    >

      {/* ══════════ PAGE 1: Photo | Info ══════════ */}
      <div className="resume-page" style={{ width: PAGE_W, height: PAGE_H, display: 'flex', flexDirection: 'row', overflow: 'hidden', flexShrink: 0 }}>

        {/* Left: portfolio photo + quote overlay */}
        <div style={{ width: P1_PHOTO_W, height: PAGE_H, position: 'relative', flexShrink: 0, overflow: 'hidden' }}>
          {photo1 ? (
            <img src={photo1} alt="포트폴리오" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', background: 'linear-gradient(155deg,#2d404e 0%,#415462 48%,#1c2c3a 100%)' }} />
          )}
          {visibility.introduction && introText && (
            <div style={{
              position: 'absolute', bottom: 26, left: 22, right: 22,
              background: 'rgba(200,213,224,0.83)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              borderRadius: 7, padding: '18px 24px',
              textAlign: 'center', color: '#1b2936',
              fontSize: 18, fontWeight: 100, lineHeight: 1.78, letterSpacing: '-.01em',
              fontFamily: NOTO,
              overflow: 'hidden',
              display: '-webkit-box' as any,
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical' as any,
            }}>
              &ldquo;{introText}&rdquo;
            </div>
          )}
        </div>

        {/* Right: dark info panel */}
        <div style={{ ...dark(P1_INFO_W), padding: '40px 32px 32px' }}>
          {nameEn && (
            <div style={{
              fontFamily: NOTO_EN, fontSize: 32, fontWeight: 100,
              letterSpacing: '.13em', color: TEXT_WHITE, lineHeight: 1.15, marginBottom: 9,
            }}>
              {nameEn}
            </div>
          )}
          {desiredPosition && (
            <div style={{ fontSize: 15, color: TEXT_LABEL, fontWeight: 100, letterSpacing: '.04em', marginBottom: 30 }}>
              {desiredPosition}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {infoItems.map(item => (
              <div key={item.label}>
                <div style={{ fontSize: 13, color: TEXT_LABEL, letterSpacing: '.06em', marginBottom: 3, fontWeight: 100 }}>
                  {item.label}
                </div>
                <div style={{ fontSize: 15, color: TEXT_BODY, fontWeight: 100, lineHeight: 1.55 }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════ PAGE 2: Content | Photo ══════════ */}
      <div className="resume-page" style={{ width: PAGE_W, height: PAGE_H, display: 'flex', flexDirection: 'row', overflow: 'hidden', flexShrink: 0 }}>

        {/* Left: dark content */}
        <div style={{ ...dark(P2_CONTENT_W), padding: '28px 30px 24px 28px' }}>
          {hasEdu && (
            <SectionRow title="학력" showDivider={false}>
              {colleges.length > 0 && (
                <div>
                  <div style={{ fontSize: 15, fontWeight: 100, color: TEXT_BODY, marginBottom: 6 }}>대학교</div>
                  {colleges.map((edu, i) => (
                    <div key={edu.id} style={{ marginBottom: i < colleges.length - 1 ? 10 : 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14, color: TEXT_LABEL, fontWeight: 100, width: 38, flexShrink: 0 }}>{edu.year}</span>
                        <span style={{ fontSize: 14, color: TEXT_BODY, fontWeight: 100 }}>{edu.schoolName}</span>
                        {edu.degreeReviewStatus === 'APPROVED' && <Badge3 label="학위 인증 완료" variant="degree" />}
                      </div>
                      <div style={{ fontSize: 13, color: TEXT_MUTED, fontWeight: 100, marginTop: 2, marginLeft: 46 }}>
                        {edu.major} {STATUS_LABEL[edu.status] || edu.status}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {graduates.length > 0 && (
                <div style={{ marginTop: colleges.length > 0 ? 8 : 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 100, color: TEXT_BODY, marginBottom: 6 }}>대학원</div>
                  {graduates.map((edu, i) => (
                    <div key={edu.id} style={{ marginBottom: i < graduates.length - 1 ? 10 : 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14, color: TEXT_LABEL, fontWeight: 100, width: 38, flexShrink: 0 }}>{edu.year}</span>
                        <span style={{ fontSize: 14, color: TEXT_BODY, fontWeight: 100 }}>{edu.schoolName}</span>
                        {edu.degreeReviewStatus === 'APPROVED' && <Badge3 label="학위 인증 완료" variant="degree" />}
                      </div>
                      <div style={{ fontSize: 13, color: TEXT_MUTED, fontWeight: 100, marginTop: 2, marginLeft: 46 }}>
                        {edu.major}{edu.degreeLevel ? ` ${DEGREE_LABEL[edu.degreeLevel]}` : ''} {STATUS_LABEL[edu.status] || edu.status}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionRow>
          )}

          {hasExp && (
            <SectionRow title="경력" showDivider={hasEdu}>
              {experience.map(exp => (
                <Row2 key={exp.id} date={exp.year} content={`${exp.company}${exp.position ? ` ${exp.position}` : ''}`} />
              ))}
            </SectionRow>
          )}

          {hasAward && (
            <SectionRow title="수상 내역 및 위촉 사항" showDivider={hasEdu || hasExp}>
              {awards.map(a => <Row2 key={a.id} date={a.year} content={a.title} />)}
            </SectionRow>
          )}
        </div>

        {/* Right: portfolio photo 2 */}
        <PhotoPanel url={photo2} width={P2_PHOTO_W} />
      </div>

      {/* ══════════ PAGE 3: Photo | Content ══════════ */}
      <div className="resume-page" style={{ width: PAGE_W, height: PAGE_H, display: 'flex', flexDirection: 'row', overflow: 'hidden', flexShrink: 0 }}>

        {/* Left: portfolio photo 3 */}
        <PhotoPanel url={photo3} width={P3_PHOTO_W} />

        {/* Right: dark content */}
        <div style={{ ...dark(P3_CONTENT_W), padding: '28px 30px 24px 28px' }}>
          {hasCert && (
            <SectionRow title="보유 자격증 및 수료 내역" showDivider={false}>
              {certificatesList.map(cert => (
                <CertRow
                  key={cert.id}
                  date={certDate(cert.date)}
                  name={cert.name}
                  issuer={cert.issuer}
                  approved={cert.reviewStatus === 'APPROVED'}
                />
              ))}
            </SectionRow>
          )}

          {hasSF && (
            <SectionRow
              title={
                <span>
                  <span style={{ fontFamily: NOTO_EN, letterSpacing: '.05em' }}>STAMP FIT</span>
                  <br />인증 교육 내역
                </span>
              }
              titleWidth={SEC_TTL_EN_W}
              showDivider={hasCert}
            >
              {stampfitCerts.map((cert: any) => {
                const issued = cert.issued_at
                  ? new Date(cert.issued_at)
                      .toLocaleDateString('ko-KR', { year: 'numeric', month: 'numeric', day: 'numeric' })
                      .replace(/\.\s*/g, '. ').replace(/\.$/, '')
                  : '';
                const provider = cert.provider?.company_name || '';
                const hours = [
                  cert.class?.offline_hours > 0 ? `오프라인 ${cert.class.offline_hours}h` : null,
                  cert.class?.online_hours > 0 ? `온라인 ${cert.class.online_hours}h` : null,
                ].filter(Boolean).join(' / ');
                const sub = [provider, hours].filter(Boolean).join(' · ');
                return (
                  <CertRow
                    key={cert.id}
                    date={issued}
                    name={cert.class?.title || ''}
                    issuer={sub}
                    approved
                  />
                );
              })}
            </SectionRow>
          )}
        </div>
      </div>
    </div>
  );
}
